import { create } from 'zustand';

type State = {
  cache: Record<string, ArrayBuffer>;
  actions: {
    set: (attachmentId: string, data: ArrayBuffer) => void;
  };
};

const usePdfBytesStore = create<State>((set) => ({
  cache: {},
  actions: {
    set: (attachmentId, data) => set((state) => ({ cache: { ...state.cache, [attachmentId]: data } })),
  },
}));

export const usePdfBytes = (attachmentId: string) => usePdfBytesStore((state) => state.cache[attachmentId]);
export const usePdfBytesActions = () => usePdfBytesStore((state) => state.actions);

// In-flight requests are tracked outside the store so concurrent mounts share
// a single network fetch instead of each kicking off their own. Keyed by
// attachment AND url: signed URLs expire ~30s after issue, and a retry armed
// with a fresh URL must not inherit the doomed request for an expired one.
const inFlight = new Map<string, Promise<ArrayBuffer>>();

export function fetchPdfBytes(attachmentId: string, url: string): Promise<ArrayBuffer> {
  const key = `${attachmentId}|${url}`;
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = (async () => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`status ${response.status}`);
    const buffer = await response.arrayBuffer();
    usePdfBytesStore.getState().actions.set(attachmentId, buffer);
    return buffer;
  })().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}
